//#region IMPORT
import React, { useRef } from 'react';
import {
  Box,
  Button as MuiButton,
  IconButton,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Dialog } from '../MUIs';
import Global from '../../Services/Common/Global';
import Commons from '../../Services/Common/Common';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../Reducer/SnackbarReducer';
import { setFileFields, resetFileFields } from '../../Reducer/FileFieldReducer';
import FilePreview from '../FilePreview/FilePreview';
import dayjs from 'dayjs';
//#endregion

/**
 * 파일 업로드 다이얼로그 공통 컴포넌트
 * @param {boolean} props.open - 다이얼로그 열림/닫힘 상태
 * @param {Function} props.onClose - 다이얼로그 닫기 함수
 * @param {React.RefObject} props.gridRef - 그리드 ref
 * @param {Array} props.selectedRowData - 선택된 행 데이터 배열
 * @param {Function} [props.setSelectedRowData] - 선택된 행 데이터 설정 함수
 * @param {Function} props.setChangedRowData - 변경된 행 데이터 설정 함수
 * @param {Function} [props.onRowUpdated] - 행 갱신 시 부모 rowData 동기화 콜백(updatedRow) — 저장 시 수정 행 인식용
 * @param {string} props.fileNameField - 파일명 필드명
 * @param {string} [props.saveNameField='saveName'] - 저장 파일명 필드명
 * @param {string} props.filePathField - 파일경로 필드명
 * @param {Function} props.SaveLogicPreview - 미리보기 파일 저장 함수
 * @param {Function} [props.DownloadLogic] - 파일 다운로드 함수
 * @param {string} [props.accept='.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.hwp'] - 허용할 파일 확장자
 * @param {string} [props.title='파일 업로드'] - 다이얼로그 제목
 * @param {string} [props.filePreviewHeight='600px'] - 파일 미리보기 높이
 * @returns {JSX.Element} 파일 업로드 다이얼로그
 */
function FileUploadDialog({
  open,
  onClose,
  gridRef,
  selectedRowData,
  setSelectedRowData,
  setChangedRowData,
  onRowUpdated,
  fileNameField,
  saveNameField = 'saveName',
  filePathField,
  SaveLogicPreview,
  DownloadLogic,
  accept = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.hwp',
  title = '파일 업로드',
  filePreviewHeight = '600px',
  // 파일명 길이 제한 안함
  noLimitFileName = false,
}) {
  const dispatch = useDispatch();
  const { previewFilePathField, previewFileName } = useSelector(
    (state) => state.fileField
  );
  const fileUploadInputRef = useRef(null);

  // 파일 선택 버튼 클릭 핸들러
  const handleFileClick = () => {
    fileUploadInputRef.current?.click();
  };

  // 다이얼로그 저장 핸들러
  const handleFileModalSave = async () => {
    onClose();
  };

  // 파일 변경 핸들러
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!noLimitFileName && file.name.length > 100) {
      dispatch(
        showSnackbar({
          message:
            '파일명 길이가 100자를 초과합니다. 100자 이하로 파일명을 수정해 주세요.',
          type: 'warning',
        })
      );
      return;
    }

    const api = gridRef?.current?.api;
    const clickedRowData = selectedRowData?.[0];

    if (!api || !clickedRowData || !fileNameField) {
      dispatch(
        showSnackbar({
          message: '파일 정보가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }

    try {
      const fileName = file.name;
      const timestamp = dayjs().format('YYYYMMDDHHmmss');
      const saveName = `${timestamp}_${file.name}`;
      const fileToSave = new File([file], saveName, {
        type: file.type || 'application/octet-stream',
      });
      const previewPath = await SaveLogicPreview(fileToSave);

      if (!previewPath) {
        return;
      }

      let targetRowNode = null;
      api.forEachNode((node) => {
        if (node.data?.no === clickedRowData?.no) {
          targetRowNode = node;
        }
      });

      if (targetRowNode) {
        const existingFilePath = targetRowNode.data[filePathField] || '';

        const updatedData = {
          ...targetRowNode.data,
          [fileNameField]: fileName,
          [saveNameField]: saveName,
          [filePathField]: existingFilePath,
          [previewFilePathField]: previewPath,
          editYn: 'Y',
        };

        targetRowNode.setData(updatedData);

        setChangedRowData((prev) => {
          const filtered = prev.filter((row) => row.no !== clickedRowData.no);
          return [...filtered, updatedData];
        });

        // 선택된 행 데이터 업데이트
        if (setSelectedRowData) {
          setSelectedRowData([updatedData]);
        }

        // 부모 rowData 동기화 → 저장 시 수정 행 즉시 인식
        onRowUpdated?.(updatedData);

        const ext = file.name.split('.').pop()?.toLowerCase();
        let newPreviewUrl = '';
        if (['png', 'jpg', 'jpeg'].includes(ext)) {
          const reader = new FileReader();
          reader.onload = (event) => {
            newPreviewUrl = event.target.result;
            dispatch(
              setFileFields({
                previewFileName: fileName,
                previewUrl: newPreviewUrl,
              })
            );
          };
          reader.readAsDataURL(file);
        } else {
          const fixedPath = Commons.EncodeUrl(previewPath);
          newPreviewUrl = fixedPath
            ? Global.file.useUrl + 'files/' + fixedPath
            : '';
          dispatch(
            setFileFields({
              previewFileName: fileName,
              previewUrl: newPreviewUrl,
            })
          );
        }

        dispatch(
          showSnackbar({
            message: `파일이 업로드되었습니다: ${fileName}`,
            type: 'success',
          })
        );
      }
    } catch (error) {
      console.log(error);
      console.error(error);
      dispatch(
        showSnackbar({
          message: `파일 업로드 실패: ${error.message}`,
          type: 'error',
        })
      );
    } finally {
      if (fileUploadInputRef.current) {
        fileUploadInputRef.current.value = '';
      }
    }
  };

  // 파일 삭제 핸들러
  const handleFileDelete = async () => {
    dispatch(resetFileFields());

    const api = gridRef?.current?.api;
    const clickedRowData = selectedRowData?.[0];
    if (api && clickedRowData) {
      let targetRowNode = null;
      api.forEachNode((node) => {
        if (node.data?.no === clickedRowData?.no) {
          targetRowNode = node;
        }
      });

      if (targetRowNode) {
        const existingFilePath = targetRowNode.data?.[filePathField] || '';

        // 파일명만 비우고, 기존 파일 경로는 저장 시점에 삭제 처리할 수 있게 유지
        // (특정 화면에서는 저장 전에 경로까지 지우면 기존 파일을 삭제할 수 없어 동작이 꼬일 수 있음)
        // setDataValue는 해당 필드가 그리드 컬럼일 때만 사용 가능. 컬럼이 없으면 getColDef() null 에러 발생하므로 setData만 사용.
        targetRowNode.setData({
          ...targetRowNode.data,
          [fileNameField]: '',
          [filePathField]: existingFilePath,
          ...(previewFilePathField ? { [previewFilePathField]: '' } : {}),
          editYn: 'Y',
        });
        const updatedRow = {
          ...clickedRowData,
          [fileNameField]: '',
          [filePathField]: existingFilePath,
          ...(previewFilePathField ? { [previewFilePathField]: '' } : {}),
          editYn: 'Y',
        };
        setChangedRowData((prev) => {
          const filtered = prev.filter((row) => row.no !== clickedRowData.no);
          return [...filtered, updatedRow];
        });
        // 선택 행 갱신 → 미리보기 영역이 '파일 정보가 없습니다'로 바로 반영
        if (setSelectedRowData) {
          setSelectedRowData([updatedRow]);
        }

        // 부모 rowData 동기화 → 저장 시 수정 행 즉시 인식
        onRowUpdated?.(updatedRow);
      }
    }
  };
  return (
    <Dialog
      title={title}
      open={open}
      onClose={onClose}
      contentSx={{ display: 'flex', flexDirection: 'column' }}
      closeButtonName={'닫기'}
      useSaveAfterClose={true}
      maxWidth="xl"
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          padding: 2,
        },
      }}
      onSave={handleFileModalSave}
      content={
        <Box
          sx={{
            height: 'calc(100vh - 130px)',
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            gap: 2,
          }}
        >
          {/* 상단 영역 */}
          <Paper elevation={3} sx={{ p: 2, flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                placeholder="파일 선택"
                variant="outlined"
                size="small"
                sx={{
                  width: 550,
                }}
                value={previewFileName}
                inputProps={{
                  readOnly: true,
                  tabIndex: -1,
                  style: {
                    caretColor: 'transparent',
                  },
                }}
                onClick={handleFileClick}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <MuiButton
                        size="small"
                        sx={{
                          borderLeft: 1,
                          borderColor: 'divider',
                          borderRadius: 0,
                          borderTopRightRadius: '4px',
                          borderBottomRightRadius: '4px',
                          px: 1.5,
                          minWidth: 'auto',
                          fontSize: '1rem',
                        }}
                      >
                        선택
                      </MuiButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                readOnly
              />
              <IconButton
                onClick={handleFileDelete}
                sx={{
                  ml: 1,
                  color: 'red',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '50%',
                  '&:hover': {
                    backgroundColor: '#cccccc',
                  },
                }}
                disabled={
                  !previewFileName ||
                  previewFileName === '파일 선택' ||
                  previewFileName.trim() === ''
                }
              >
                <DeleteIcon />
              </IconButton>

              {/* 숨겨진 input (파일 선택) */}
              <input
                type="file"
                ref={fileUploadInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept={accept}
              />

              {DownloadLogic && (
                <MuiButton
                  variant="contained"
                  color="success"
                  onClick={DownloadLogic}
                >
                  <DownloadIcon fontSize={''} />
                  파일 다운로드
                </MuiButton>
              )}
            </Stack>
          </Paper>

          {/* 미리보기 영역 */}
          <Paper
            sx={{
              p: 2,
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              미리보기
            </Typography>
            <Box
              sx={{
                border: '1px dashed #ccc',
                borderRadius: 1,
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <FilePreview
                selectedRowData={selectedRowData}
                fileNameField={fileNameField}
                filePathField={filePathField}
                filePreviewHeight={filePreviewHeight}
              />
            </Box>
          </Paper>
        </Box>
      }
    />
  );
}

export default FileUploadDialog;
